import {
  collection,
  getDocs,
  doc,
  getDoc,
  query,
  where,
  updateDoc,
  orderBy,
  limit
} from 'firebase/firestore';
import { db } from '../../firebase';
import datatableProfiler from '../datatable/utils/DatatablePerformanceProfiler';

/**
 * Obtiene la lista de clientes disponibles para un servicio, filtrados por los permisos del usuario
 * @param {string} serviceId - ID del servicio
 * @param {Array} clientesPermitidos - IDs de clientes a los que el usuario tiene acceso
 * @returns {Promise<Array>} - Lista de clientes
 */
export const fetchClientes = async (serviceId, clientesPermitidos = []) => {
  if (!serviceId) {
    throw new Error('ID de servicio no proporcionado');
  }

  try {
    const clientesRef = collection(db, "servicios", serviceId, "clientes");
    const clientesSnapshot = await getDocs(clientesRef);

    const todosClientes = clientesSnapshot.docs.map(doc => ({
      id: doc.id,
      nombre: doc.data().nombre || 'Cliente sin nombre',
      ...doc.data()
    }));

    // Si no hay lista de clientes permitidos, devolver todos
    if (!clientesPermitidos || clientesPermitidos.length === 0) {
      return todosClientes;
    }

    // Filtrar para devolver solo los clientes a los que el usuario tiene acceso
    return todosClientes.filter(cliente => clientesPermitidos.includes(cliente.id));
  } catch (error) {
    console.error("Error al obtener clientes:", error);
    throw error;
  }
};

/**
 * Obtiene las tiendas/servicios de un cliente específico, filtrados por permisos del usuario
 * @param {string} serviceId - ID del servicio
 * @param {string} clienteNombre - Nombre del cliente
 * @param {Array} tiendasPermitidas - IDs de tiendas a las que el usuario tiene acceso
 * @returns {Promise<Array>} - Lista de tiendas
 */
export const fetchTiendasPorCliente = async (serviceId, clienteNombre, tiendasPermitidas = []) => {
  if (!serviceId || !clienteNombre) {
    return [];
  }

  try {
    const tiendasRef = collection(db, "servicios", serviceId, "tiendas");
    const tiendasQuery = query(
      tiendasRef,
      where("nombreCliente", "==", clienteNombre),
      limit(100) // Aumentar límite para asegurar que obtenemos todas las tiendas relevantes
    );

    const tiendasSnapshot = await getDocs(tiendasQuery);

    if (tiendasSnapshot.empty) {
      console.log(`No se encontraron tiendas para el cliente: ${clienteNombre}`);
      return [];
    }

    // Procesar resultados de tiendas
    const todasTiendas = tiendasSnapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        nombre: data.numTienda || 'Tienda sin nombre',
        numero: data.numTienda,
        ...data
      };
    });

    // Si no hay lista de tiendas permitidas, devolver todas
    if (!tiendasPermitidas || tiendasPermitidas.length === 0) {
      return todasTiendas;
    }

    // Filtrar para devolver solo las tiendas a las que el usuario tiene acceso
    return todasTiendas.filter(tienda => tiendasPermitidas.includes(tienda.id));
  } catch (error) {
    console.error("Error al obtener tiendas:", error);
    return [];
  }
};

/**
 * Obtiene los números de tienda asignados a un administrador
 * @param {string} serviceId - ID del servicio
 * @param {Array} tiendaIds - IDs de tiendas del administrador
 * @returns {Promise<Array>} - Lista de números de tienda
 */
export const fetchTiendasAdmin = async (serviceId, tiendaIds) => {
  if (!serviceId || !tiendaIds || !tiendaIds.length) {
    return [];
  }

  try {
    const tiendasNumeros = await Promise.all(
      tiendaIds.map(async (tiendaId) => {
        const tiendaRef = doc(db, "servicios", serviceId, "tiendas", tiendaId);
        const tiendaDoc = await getDoc(tiendaRef);

        if (tiendaDoc && tiendaDoc.exists()) {
          return {
            id: tiendaId,
            numero: tiendaDoc.data().numTienda,
            nombre: tiendaDoc.data().nombreTienda,
            cliente: tiendaDoc.data().nombreCliente
          };
        }

        return null;
      })
    );

    return tiendasNumeros.filter(Boolean);
  } catch (error) {
    console.error("Error al obtener números de tiendas:", error);
    throw error;
  }
};

/**
 * Obtiene vigilantes filtrados por cliente y/o servicio con soporte para múltiples selecciones
 * @param {Object} params - Parámetros de búsqueda
 * @param {string} params.serviceId - ID del servicio
 * @param {Object} params.filters - Filtros (clientes, servicios)
 * @param {Array} params.clientesPermitidos - IDs de clientes permitidos
 * @param {Array} params.tiendasPermitidas - IDs de tiendas permitidas
 * @returns {Promise<Array>} - Lista de vigilantes filtrados
 */
export const fetchVigilantesFiltrados = async ({
  serviceId,
  filters = {},
  clientesPermitidos = [],
  tiendasPermitidas = []
}) => {
  if (!serviceId) {
    throw new Error('ID de servicio no proporcionado');
  }

  try {
    // Verificar si tenemos filtros activos
    const hasClienteFilter = filters.clientes && filters.clientes.length > 0;
    const hasServicioFilter = filters.servicios && filters.servicios.length > 0;
    const showAll = filters.cliente === 'todos';

    // Si no hay filtros y no es "mostrar todos", no cargamos datos
    if (!hasClienteFilter && !hasServicioFilter && !showAll) {
      return [];
    }

    // 📊 Medir carga de tiendas permitidas
    datatableProfiler.startMeasurement('01a_FetchTiendasPermitidas', {
      tiendasCount: tiendasPermitidas.length
    });

    // Primero, obtener todas las tiendas permitidas para luego filtrar vigilantes
    let tiendasPermitidas_data = [];
    if (tiendasPermitidas && tiendasPermitidas.length > 0) {
      const tiendasRefs = tiendasPermitidas.map(tiendaId =>
        doc(db, "servicios", serviceId, "tiendas", tiendaId)
      );

      // Obtener documentos en lotes para reducir consultas
      const tiendasDocs = await Promise.all(
        tiendasRefs.map(async tiendaRef => {
          const tiendaDoc = await getDoc(tiendaRef);
          if (tiendaDoc.exists()) {
            return {
              id: tiendaDoc.id,
              ...tiendaDoc.data()
            };
          }
          return null;
        })
      );

      tiendasPermitidas_data = tiendasDocs.filter(Boolean);
    }

    datatableProfiler.endMeasurement('01a_FetchTiendasPermitidas', {
      tiendasCargadas: tiendasPermitidas_data.length
    });

    // Números de tienda permitidos para filtrar rápidamente
    const numerosPermitidos = tiendasPermitidas_data.map(t => t.numTienda);

    // 📊 Medir carga de vigilantes base
    datatableProfiler.startMeasurement('01b_FetchVigilantesBase', {
      serviceId
    });

    // Obtener todos los vigilantes
    const vigilantesRef = collection(db, "servicios", serviceId, "vigilantes");
    const vigilantesSnapshot = await getDocs(vigilantesRef);

    datatableProfiler.endMeasurement('01b_FetchVigilantesBase', {
      vigilantesTotales: vigilantesSnapshot.docs.length
    });

    // 📊 Medir procesamiento y población de vigilantes
    datatableProfiler.startMeasurement('01c_ProcessVigilantes', {
      vigilantesToProcess: vigilantesSnapshot.docs.length
    });

    // Procesar vigilantes
    const vigilantesList = await Promise.all(vigilantesSnapshot.docs.map(async (doc) => {
      const vigilante = { id: doc.id, ...doc.data() };

      // Verificar si el vigilante tiene tiendas asignadas
      if (!vigilante.tiendas || vigilante.tiendas.length === 0) {
        return null; // Excluir vigilantes sin tiendas
      }

      // Filtrar las tiendas del vigilante para incluir solo las permitidas
      const tiendasVigilante = vigilante.tiendas.filter(tienda =>
        !tiendasPermitidas.length || numerosPermitidos.includes(tienda.numero)
      );

      // Si después de filtrar no quedan tiendas, excluir el vigilante
      if (tiendasVigilante.length === 0) {
        return null;
      }

      // Aplicar filtros de usuario
      if (hasServicioFilter && !tiendasVigilante.some(t => filters.servicios.includes(t.numero))) {
        return null;
      }

      // Obtener información completa de las tiendas del vigilante
      const vigilanteTiendas = await Promise.all(tiendasVigilante.map(async (tienda) => {
        const tiendaNumero = tienda.numero;

        // Buscar primero en la lista de tiendas permitidas que ya tenemos
        const tiendaExistente = tiendasPermitidas_data.find(t => t.numTienda === tiendaNumero);
        if (tiendaExistente) {
          return tiendaExistente;
        }

        // Si no se encuentra, consultar la base de datos
        const tiendasQuery = query(
          collection(db, "servicios", serviceId, "tiendas"),
          where("numTienda", "==", tiendaNumero || ""),
          limit(1)
        );

        const tiendasSnapshot = await getDocs(tiendasQuery);
        const tiendaDoc = tiendasSnapshot.docs[0];

        if (tiendaDoc && tiendaDoc.exists()) {
          return {
            ...tiendaDoc.data(),
            id: tiendaDoc.id
          };
        }

        return null;
      }));

      // Filtrar tiendas nulas
      const validTiendas = vigilanteTiendas.filter(Boolean);

      // Si no hay tiendas válidas después de filtrar, excluir el vigilante
      if (validTiendas.length === 0) {
        return null;
      }

      // Filtrar por cliente
      if (hasClienteFilter && !validTiendas.some(t => filters.clientes.includes(t.nombreCliente))) {
        return null;
      }

      // Preparar lista de clientes únicos para este vigilante
      const uniqueClientes = Array.from(new Set(
        validTiendas.map(t => t.nombreCliente)
      )).filter(Boolean);

      // Devolver vigilante con información adicional
      return {
        ...vigilante,
        tiendas: validTiendas.map(t => ({
          id: t.id,
          numero: t.numTienda,
          nombre: t.nombreTienda || t.numTienda
        })),
        clientes: uniqueClientes
      };
    }));

    // Filtrar vigilantes nulos (excluidos por filtros)
    const vigilantesFiltrados = vigilantesList.filter(Boolean);

    datatableProfiler.endMeasurement('01c_ProcessVigilantes', {
      vigilantesProcessed: vigilantesFiltrados.length,
      vigilantesExcluidos: vigilantesList.length - vigilantesFiltrados.length
    });

    return vigilantesFiltrados;
  } catch (error) {
    console.error("Error al obtener vigilantes filtrados:", error);
    throw error;
  }
};

/**
 * Actualiza el estado de un vigilante (borrado)
 * @param {string} serviceId - ID del servicio
 * @param {string} vigilanteId - ID del vigilante
 * @param {boolean} borrado - Estado de borrado
 * @returns {Promise<void>}
 */
export const updateVigilanteEstado = async (serviceId, vigilanteId, borrado) => {
  if (!serviceId || !vigilanteId) {
    throw new Error('IDs no proporcionados');
  }

  try {
    const vigilanteRef = doc(db, "servicios", serviceId, "vigilantes", vigilanteId);
    await updateDoc(vigilanteRef, { borrado });
  } catch (error) {
    console.error(`Error al ${borrado ? 'borrar' : 'restaurar'} vigilante:`, error);
    throw error;
  }
};

/**
 * Obtiene todos los vigilantes sin filtros de permisos
 * @param {string} serviceId - ID del servicio
 * @returns {Promise<Array>} - Lista de todos los vigilantes
 */
export const fetchAllVigilantes = async (serviceId) => {
  if (!serviceId) {
    throw new Error('ID de servicio no proporcionado');
  }

  try {
    // 📊 Medir carga de todos los vigilantes
    datatableProfiler.startMeasurement('03a_FetchAllVigilantesBase', {
      serviceId
    });

    // Obtener todos los vigilantes sin aplicar filtros
    const vigilantesRef = collection(db, "servicios", serviceId, "vigilantes");
    const vigilantesSnapshot = await getDocs(vigilantesRef);

    datatableProfiler.endMeasurement('03a_FetchAllVigilantesBase', {
      vigilantesTotales: vigilantesSnapshot.docs.length
    });

    // 📊 Medir procesamiento de vigilantes
    datatableProfiler.startMeasurement('03b_ProcessAllVigilantes', {
      vigilantesToProcess: vigilantesSnapshot.docs.length
    });

    // Procesar vigilantes
    const vigilantesList = await Promise.all(vigilantesSnapshot.docs.map(async (doc) => {
      const vigilante = { id: doc.id, ...doc.data() };

      // Si el vigilante no tiene tiendas, devolver solo la información básica
      if (!vigilante.tiendas || vigilante.tiendas.length === 0) {
        return vigilante;
      }

      // Obtener información completa de las tiendas del vigilante
      const vigilanteTiendas = await Promise.all(vigilante.tiendas.map(async (tienda) => {
        const tiendaNumero = tienda.numero;

        // Consultar la base de datos para obtener información completa
        const tiendasQuery = query(
          collection(db, "servicios", serviceId, "tiendas"),
          where("numTienda", "==", tiendaNumero || ""),
          limit(1)
        );

        const tiendasSnapshot = await getDocs(tiendasQuery);
        const tiendaDoc = tiendasSnapshot.docs[0];

        if (tiendaDoc && tiendaDoc.exists()) {
          return {
            ...tiendaDoc.data(),
            id: tiendaDoc.id
          };
        }

        return { numero: tiendaNumero };
      }));

      // Filtrar tiendas nulas
      const validTiendas = vigilanteTiendas.filter(Boolean);

      // Preparar lista de clientes únicos para este vigilante
      const uniqueClientes = Array.from(new Set(
        validTiendas
          .map(t => t.nombreCliente)
          .filter(Boolean)
      ));

      // Devolver vigilante con información adicional
      return {
        ...vigilante,
        tiendas: validTiendas.map(t => ({
          id: t.id,
          numero: t.numTienda || t.numero,
          nombre: t.nombreTienda || t.numTienda || t.numero
        })),
        clientes: uniqueClientes
      };
    }));

    datatableProfiler.endMeasurement('03b_ProcessAllVigilantes', {
      vigilantesProcessed: vigilantesList.length,
      conTiendas: vigilantesList.filter(v => v.tiendas?.length > 0).length
    });

    return vigilantesList;
  } catch (error) {
    console.error("Error al obtener todos los vigilantes:", error);
    throw error;
  }
};

/**
 * Obtiene vigilantes asociados al admin actual (comportamiento original)
 * @param {string} serviceId - ID del servicio
 * @param {Object} adminData - Datos del administrador actual
 * @returns {Promise<Array>} - Lista de vigilantes del admin
 */
export const fetchAdminVigilantes = async (serviceId, adminData) => {
  if (!serviceId) {
    throw new Error('ID de servicio no proporcionado');
  }

  try {
    // Esta es la forma en que funcionaba originalmente
    return fetchVigilantesFiltrados({
      serviceId,
      filters: { cliente: 'todos' },
      clientesPermitidos: adminData?.clientes || [],
      tiendasPermitidas: adminData?.tiendas || []
    });
  } catch (error) {
    console.error("Error al obtener vigilantes del admin:", error);
    throw error;
  }
};